import React, {useState} from 'react';
import {injectIntl} from 'react-intl';
import GenericModal from './generic_modal';
import Button from '../../forecast-app/shared/components/buttons/button/button';
import {BUTTON_STYLE, BUTTON_COLOR, ELEMENT_TYPE} from '../../constants';
import HeaderBar from '../../forecast-app/shared/components/headers/header-bar/header_bar';
import Util from '../../forecast-app/shared/util/util';
import UploadingOverlay from '../../forecast-app/shared/components/uploading-overlay/uploading_overlay';
import UpdateCompanySubscriptionMutation from '../../mutations/update_company_subscription_mutation';

const plans = {
	LITE: 'LITE',
	PRO: 'PRO',
	ENTERPRISE: 'ENTERPRISE',
};

const billingPeriods = {
	MONTHLY: 'MONTHLY',
	QUARTERLY: 'QUARTERLY',
	YEARLY: 'YEARLY',
};

const UpgradeSubscriptionModalNew = ({companyId, country, closeModal, intl, subscription, planList, addonList}) => {
	const [paymentInterval] = useState(billingPeriods.YEARLY);
	const [currentPlan, setCurrentPlan] = useState(subscription.plan);
	const [loading, setLoading] = useState(false);
	const currency = subscription.currency;
	const currencySymbol = Util.GetCurrencySymbol(currency);
	const virtualUserPrice = addonList.find(addon => addon.chargebeeId.includes('virtual_user') && addon.currency === currency).price / 100;

	const getSeatPrice = subscription =>
		subscription.billingPeriodType === 'YEAR' ? subscription.seatPrice / (12 * subscription.billingPeriod) : subscription.billingPeriodType === 'MONTH' ? subscription.seatPrice / subscription.billingPeriod : null;

	const [currentSeatPrice, setCurrentSeatPrice] = useState(getSeatPrice(subscription));

	const getActiveCoupon = () => {
		const activeCoupon = subscription.coupons ? subscription.coupons[0] : null;

		let coupon = {
			couponId: activeCoupon !== null ? activeCoupon.id : null,
			couponText: activeCoupon !== null ? '' : '',
			percentOff: activeCoupon !== null ? activeCoupon.percentOff : '',
		};

		return coupon;
	};

	const getShownPlan = plan => {
		return planList.find(p => p.chargebeeId.includes(plan.toLowerCase()) && p.currency === currency && p.chargebeeId.includes(paymentInterval.toLowerCase()));
	};

	const processPlanUpdate = plan => {
		setLoading(true);
		const newPlan = getShownPlan(plan).chargebeeId;

		const onSuccess = () => {
			setCurrentPlan(newPlan.toUpperCase());
			const shownPlan = getShownPlan(plan);
			const newSubscription = {
				billingPeriodType: shownPlan.periodUnit,
				billingPeriod: shownPlan.period,
				seatPrice: shownPlan.price / 100,
			};
			setCurrentSeatPrice(getSeatPrice(newSubscription));
			setLoading(false);
		};

		Util.CommitMutation(
			UpdateCompanySubscriptionMutation,
			{
				id: companyId,
				plan: newPlan,
				coupon: getActiveCoupon(plan).couponId,
				currency: currency,
				virtualSeats: subscription.virtualSeats,
			},
			onSuccess
		);
	};

	const constructHeader = () => {
		const leftContent = [];

		leftContent.push({
			type: ELEMENT_TYPE.TITLE,
			intl: intl,
			textId: 'settings.subscription.upgrade_plan_title',
		});

		return <HeaderBar leftContent={leftContent} />;
	};

	const constructPlans = () => {
		const billingPeriodChanged = !currentPlan.includes(paymentInterval);
		const constructUpgradeButton = plan => {
			let buttonText;
			let buttonAction;
			let buttonCY;
			if (!currentPlan.includes(plan)) {
				buttonText = intl.formatMessage({id: 'settings_subscription.upgrade'}).toUpperCase();
				buttonAction = () => {
					processPlanUpdate(plan);
				};
				buttonCY = 'upgrade-to-plan-button';
			} else {
				buttonText =
					paymentInterval === billingPeriods.YEARLY
						? intl.formatMessage({id: 'settings_subscription.switch_annual_billing_period'}).toUpperCase()
						: paymentInterval === billingPeriods.QUARTERLY
						? intl.formatMessage({id: 'settings_subscription.switch_quarterly_billing_period'}).toUpperCase()
						: intl.formatMessage({id: 'settings_subscription.switch_monthly_billing_period'}).toUpperCase();
				buttonAction = () => {
					processPlanUpdate(plan);
				};
				buttonCY = 'switch-plan-billing-button';
			}
			return <Button cy={buttonCY} className="change-subscription-button" buttonStyle={BUTTON_STYLE.OUTLINE} colorTheme={BUTTON_COLOR.PURPLE} text={buttonText} onClick={buttonAction} />;
		};

		const getActivePlanIndex = () => {
			for (let [index, value] of Object.values(plans).entries()) {
				if (currentPlan.includes(value)) {
					return index;
				}
			}
		};
		const isPlanActive = (plan, shownSubscription = null, checkPeriod = false) => {
			if (shownSubscription && checkPeriod) {
				const planPeriod = shownSubscription.chargebeeId.includes('monthly') ? 'MONTHLY' : shownSubscription.chargebeeId.includes('quarterly') ? 'QUARTERLY' : shownSubscription.chargebeeId.includes('yearly') ? 'YEARLY' : '';
				return currentPlan.includes(plan) && currentPlan.includes(planPeriod);
			} else {
				return currentPlan.includes(plan);
			}
		};
		const getPlanNameId = plan => {
			switch (plan) {
				case plans.ENTERPRISE:
					return 'enterprise_plan_name';
				case plans.LITE:
					return 'lite_plan_name';
				case plans.PRO:
					return 'pro_plan_name';
				default:
					return null;
			}
		};

		const getPlanFeatures = plan => {
			if (plan !== plans.LITE) {
				return (
					<ul>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.feature_retainers'})}
						</li>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.feature_premium_integrations'})}
						</li>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.feature_complience'})}
						</li>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.financials'})}
						</li>
						{plan === plans.ENTERPRISE ? (
							<>
								<li>
									<span>+</span> {intl.formatMessage({id: 'common.feature_single_sign_on'})}
								</li>
								<li>
									<span>+</span> {intl.formatMessage({id: 'common.feature_custom_sla'})}
								</li>
							</>
						) : null}
					</ul>
				);
			}
		};

		const getTotalSeatPrice = plan => {
			const shownPlan = getShownPlan(plan);
			let pricePerSeat = null;
			if (isPlanActive(plan, shownPlan, true)) {
				pricePerSeat = currentSeatPrice;
			} else {
				pricePerSeat = shownPlan.periodUnit === 'YEAR' ? shownPlan.price / shownPlan.period / 12 / 100 : shownPlan.price / shownPlan.period / 100;
			}

			return pricePerSeat * subscription.userSeats;
		};

		const getTotalVirtualSeatPrice = () => {
			return subscription.virtualSeats * virtualUserPrice;
		};

		const getDiscountedAmount = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			return getActiveCoupon(plan).percentOff ? (amount / 100) * getActiveCoupon(plan).percentOff : amount;
		};

		const getDiscount = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			return getActiveCoupon(plan).percentOff ? (amount / 100) * getActiveCoupon(plan).percentOff : 0;
		};

		const averageSeatPricePerMonth = plan => {
			return getTotalSeatPrice(plan) / subscription.userSeats;
		};

		const getVATAmount = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			const amountWithDiscount = amount - getDiscount(plan);
			const monthlyVAT = amountWithDiscount * 0.25;
			return monthlyVAT;
		};
		const getTotal = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			const amountWithDiscount = amount - getDiscount(plan);
			const totalIncludingVAT = amountWithDiscount * (country === 'DK' ? 1.25 : 1);
			return paymentInterval === billingPeriods.YEARLY ? totalIncludingVAT * 12 : totalIncludingVAT;
		};

		return (
			<div className="subscription-plan">
				{Object.keys(plans).map((plan, index) => {
					return getActivePlanIndex() <= index && (paymentInterval === 'YEARLY' || plan !== plans.ENTERPRISE) ? (
						<div data-cy={plan + '-plan'} key={index} className={'plan ' + plan + '-plan ' + (isPlanActive(plan) ? 'active-plan' : 'inactive-plan') + (billingPeriodChanged ? ' switch-available' : '')}>
							<div className="plan-header">
								{isPlanActive(plan) ? intl.formatMessage({id: 'settings.subscription.your_plan'}).toUpperCase() : null}
								{isPlanActive(plan) && billingPeriodChanged
									? currentPlan.includes(billingPeriods.MONTHLY)
										? ' (' + intl.formatMessage({id: 'common.monthly'}).toUpperCase() + ')'
										: currentPlan.includes(billingPeriods.QUARTERLY)
										? ' (' + intl.formatMessage({id: 'common.quarterly'}).toUpperCase() + ')'
										: ' (' + intl.formatMessage({id: 'common.annual'}).toUpperCase() + ')'
									: null}
							</div>
							<div className="plan-body">
								<div className="plan-title-section">
									<div className="plan-title">{intl.formatMessage({id: 'select_plan.' + getPlanNameId(plan)}).toUpperCase()}</div>
									<div className="price-info">
										<div className="discount">{getActiveCoupon(plan) ? getActiveCoupon(plan).couponText : ''}</div>
										<div className={'seat-price'}>{currencySymbol + intl.formatNumber(isPlanActive(plan, getShownPlan(plan), true) ? currentSeatPrice : averageSeatPricePerMonth(plan), {format: 'rounded_two_decimal'})}</div>
										<div className="seat-month">{intl.formatMessage({id: 'select_plan.seat'}).toLowerCase() + '/' + intl.formatMessage({id: 'select_plan.Month'}).toLowerCase()}</div>
									</div>
									<div className="change-subscription-button-wrapper">{getActivePlanIndex() < index || billingPeriodChanged ? constructUpgradeButton(plan) : null}</div>
								</div>
								<div className="plan-feature-section">
									<div className="standard-feature">{intl.formatMessage({id: 'settings.subscription.all_standard_features'})}</div>
									{getPlanFeatures(plan)}
								</div>
								<div className="plan-calculation-section">
									<div className="calculator-title">{intl.formatMessage({id: 'settings.subscription.your_plan'})}</div>
									<table>
										<tbody>
											<tr>
												<th>{intl.formatMessage({id: 'settings_subscription.users'}).toUpperCase()}</th>
												<th>{intl.formatMessage({id: 'select_plan.seat'}).toUpperCase()}</th>
												<th>{intl.formatMessage({id: 'settings_subscription.amount'}).toUpperCase()}</th>
											</tr>
											<tr>
												<td>
													{subscription.userSeats} {intl.formatMessage({id: 'settings_subscription.users'})}
												</td>
												<td>{currencySymbol + intl.formatNumber(isPlanActive(plan, getShownPlan(plan), true) ? currentSeatPrice : averageSeatPricePerMonth(plan), {format: 'rounded_two_decimal'})}</td>
												<td>{currencySymbol + intl.formatNumber(getTotalSeatPrice(plan), {format: 'rounded_two_decimal'})}</td>
											</tr>
											<tr>
												<td>
													{subscription.virtualSeats} {intl.formatMessage({id: 'settings_subscription.virtual_users'})}
												</td>
												<td>{currencySymbol + virtualUserPrice}</td>
												<td>{currencySymbol + intl.formatNumber(getTotalVirtualSeatPrice(), {format: 'rounded_two_decimal'})}</td>
											</tr>
											{getActiveCoupon(plan) !== null && getActiveCoupon(plan).percentOff > 0 ? (
												<tr className="discount">
													<td>{intl.formatMessage({id: 'project_section.xero_modal_table_discount'})}</td>
													<td>{getActiveCoupon(plan).percentOff}%</td>
													<td>- {currencySymbol + intl.formatNumber(getDiscountedAmount(plan), {format: 'rounded_two_decimal'})}</td>
												</tr>
											) : null}
											{country === 'DK' ? (
												<tr className="vat">
													<td>VAT</td>
													<td>25%</td>
													<td>{currencySymbol + intl.formatNumber(getVATAmount(plan), {format: 'rounded_two_decimal'})}</td>
												</tr>
											) : null}
										</tbody>
									</table>
									{paymentInterval === 'YEARLY' ? (
										<div className="subtotal">
											<div>{currencySymbol + intl.formatMessage({id: 'settings_subscription_subtotal_monthly'})}</div>
											<div>{currencySymbol + intl.formatNumber(getTotal(plan) / 12, {format: 'rounded_two_decimal'})}</div>
										</div>
									) : null}
									<div className="calculator-line"></div>
									<div className="calculator-total">
										<div className="total">
											{intl.formatMessage({id: 'common.total_column'})} {paymentInterval === billingPeriods.YEARLY ? intl.formatMessage({id: 'common.annual'}) : intl.formatMessage({id: 'common.monthly'})}
										</div>
										<div className="amount">{currencySymbol + intl.formatNumber(getTotal(plan), {format: 'rounded_two_decimal'})}</div>
									</div>
								</div>
							</div>
						</div>
					) : null;
				})}
			</div>
		);
	};

	const content = () => {
		return (
			<div className="upgrade-subscription-modal">
				{loading ? <UploadingOverlay /> : null}
				<div className="header-container">{constructHeader()}</div>
				<div className="upgrade-subscription-modal-content">{constructPlans()}</div>
			</div>
		);
	};
	return <GenericModal cy="upgrade-subscription-modal" closeModal={closeModal} content={content()} />;
};

export default injectIntl(UpgradeSubscriptionModalNew);
